import { Box, HStack, Icon, Spacer, Text } from '@chakra-ui/react';
import {
  CollapsedText,
  InDesktop,
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerBody,
  ModalOrDrawerCloseButton,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@cronos-app/components';
import { ChevronRight, Tick, WalletCard } from '@cronos-app/icons';
import { useCdcDepositAddress } from '@ui/views/card-cronos/core/utils';
import { useTranslations } from 'next-intl';
import { Fragment, useState } from 'react';

import { HomeDivider } from '../shared/HomeDivider';

export const DepositAddressSelector = ({
  allDepositAddresses,
  select,
  onSelect,
}: {
  allDepositAddresses: string[];
  select: string;
  onSelect: (addr: string) => void;
}) => {
  const t = useTranslations();
  const { data: curAddr } = useCdcDepositAddress();
  const [selectOpen, setSelectOpen] = useState(false);
  if (allDepositAddresses.length <= 1) return <></>;
  return (
    <>
      <HStack
        gap="1"
        cursor="pointer"
        onClick={() => setSelectOpen(true)}
        textStyle="body2Bold"
      >
        <Icon as={WalletCard} boxSize="5" />
        {!select ? (
          <Text>{t('card.All wallets')}</Text>
        ) : (
          <>
            <Text>{t('card.Wallets')} (</Text>
            <CollapsedText
              w={{
                base: '100px',
                desktop: '120px',
              }}
              remain={4}
              text={select}
            />
            <Text>)</Text>
          </>
        )}
        <Icon as={ChevronRight} boxSize="5" transform="rotate(90deg)" />
      </HStack>
      <ModalOrDrawer
        size={{
          base: 'full',
          desktop: 'sm',
        }}
        placement="bottom"
        isOpen={selectOpen}
        onClose={() => setSelectOpen(false)}
      >
        <ModalOrDrawerOverlay />
        <ModalOrDragDrawerContent
          p="0"
          hideTopBorder
          bg="linear-gradient(0deg, #152C4B 0%, #081020 100%)"
        >
          <ModalOrDrawerHeader>
            <Text
              bg="gradients.white-blue-title"
              bgClip="text"
              textStyle={{
                base: 'body1Bold',
                desktop: 'subtitle3',
              }}
            >
              {t('card.Select wallet')}
            </Text>
            <InDesktop>
              <ModalOrDrawerCloseButton />
            </InDesktop>
          </ModalOrDrawerHeader>
          <ModalOrDrawerBody
            p={0}
            display="flex"
            flexDir="column"
            color="brand.white"
          >
            <SelectItem
              depositAddress=""
              curDepositAddress={curAddr ?? undefined}
              onSelect={(addr) => {
                onSelect(addr);
                setSelectOpen(false);
              }}
              select={select}
            />
            {allDepositAddresses.map((addr) => (
              <Fragment key={addr}>
                <HomeDivider />
                <SelectItem
                  depositAddress={addr}
                  curDepositAddress={curAddr ?? undefined}
                  onSelect={(addr) => {
                    onSelect(addr);
                    setSelectOpen(false);
                  }}
                  select={select}
                />
              </Fragment>
            ))}
          </ModalOrDrawerBody>
        </ModalOrDragDrawerContent>
      </ModalOrDrawer>
    </>
  );
};

const SelectItem = ({
  depositAddress,
  curDepositAddress,
  onSelect,
  select,
}: {
  depositAddress: string;
  curDepositAddress?: string;
  onSelect: (addr: string) => void;
  select: string;
}) => {
  const t = useTranslations();
  const isSelected =
    depositAddress.toLowerCase() === curDepositAddress?.toLowerCase();
  return (
    <HStack
      py="4"
      px="6"
      cursor="pointer"
      onClick={() => {
        onSelect(depositAddress);
      }}
    >
      {depositAddress ? (
        <CollapsedText
          w={{
            base: '100px',
            desktop: '120px',
          }}
          remain={4}
          text={depositAddress}
          textStyle="body2Bold"
        />
      ) : (
        <Text textStyle="body2Bold">{t('card.All wallets')}</Text>
      )}
      <Spacer />
      {!!depositAddress && (
        <Box
          rounded="4px"
          padding="2px 8px"
          textStyle="caption"
          border={isSelected ? 'unset' : '1px solid white'}
          color={isSelected ? 'brand.background-black' : 'brand.white'}
          bg={isSelected ? 'gradients.blue-green-horizontal' : 'unset'}
          mr="2"
        >
          {isSelected ? t('card.Linked') : t('card.Unlinked')}
        </Box>
      )}
      {depositAddress.toLowerCase() === select.toLowerCase() ? (
        <Icon as={Tick} boxSize="5" />
      ) : (
        <Box boxSize="5" />
      )}
    </HStack>
  );
};
